import { StatefulChatClient } from "@azure/communication-react";
import { useEffect } from "react";
import { initializeThreadState } from "../../utils/chatUtils";
import { ChatThreadCreatedEvent } from "@azure/communication-chat";
import { ChatThreadDeletedEvent, ChatThreadPropertiesUpdatedEvent } from "@azure/communication-signaling";
import { useChatThreadIds } from "./useChatThreadIds";
import { usePrevious } from "@fluentui/react-hooks";


const fetchThreadState = async (client: StatefulChatClient, threadId: string) => {
    const threadClient = client.getChatThreadClient(threadId);
    await initializeThreadState(threadClient);
}

export const useRefreshThreadStates = (chatClient: StatefulChatClient) => {
    // const chatClient = useChatClient();

    const threadIds = useChatThreadIds(chatClient);
    const prevThreadIds = usePrevious<string[]>(threadIds);

    useEffect(() => {

        const refreshThreads = async (threadIds: string[]) => {
            // Fetch all thread states in parallel; isolate per-thread failures so one
            // rejection doesn't abort the rest of the batch.
            await Promise.all(
                threadIds.map(threadId => fetchThreadState(chatClient, threadId).catch(console.error))
            );
        }

        if (prevThreadIds === undefined) {
            refreshThreads(threadIds).catch(console.error);
            return;
        }

        const newThreadIds = threadIds.filter(x => !prevThreadIds.includes(x));
        refreshThreads(newThreadIds).catch(console.error);

    }, [chatClient, threadIds, prevThreadIds]);
};


export const useRefreshThreadStates_alternative = (chatClient: StatefulChatClient) => {
    // NOTE: updating the thread states would be much nicer like this,
    //             but some events are somehow not triggered.
    //             Using a workaround  with `chatClient.onStateChange` and setting the threadIds manually for now.
    useEffect(() => {
        const onChatThreadCreated = (e: ChatThreadCreatedEvent) => fetchThreadState(chatClient, e.threadId);
        const onChatThreadDeleted = (e: ChatThreadDeletedEvent) => fetchThreadState(chatClient, e.threadId);
        const onChatThreadPropertiesUpdated = (e: ChatThreadPropertiesUpdatedEvent) => fetchThreadState(chatClient, e.threadId);

        chatClient.on('chatThreadCreated', (e) => onChatThreadCreated(e));
        chatClient.on('chatThreadDeleted', (e) => onChatThreadDeleted(e));
        chatClient.on('chatThreadPropertiesUpdated', (e) => onChatThreadPropertiesUpdated(e));

        return () => {
            chatClient.off('chatThreadCreated', onChatThreadCreated);
            chatClient.off('chatThreadDeleted', onChatThreadDeleted);
            chatClient.off('chatThreadPropertiesUpdated', onChatThreadPropertiesUpdated);
        };
    }, [chatClient]);

};
